import { IBudgetAttachmentFile } from "./types";

export const MAX_BUDGET_ATTACHMENTS = 8;
export const MAX_BUDGET_ATTACHMENT_SIZE = 10 * 1024 * 1024;

export const ALLOWED_BUDGET_ATTACHMENT_TYPES = [
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
  "text/csv",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const ALLOWED_BUDGET_ATTACHMENT_EXTENSIONS = [
  ".pdf",
  ".xls",
  ".xlsx",
  ".ods",
  ".csv",
  ".jpeg",
  ".jpg",
  ".png",
  ".gif",
  ".webp",
];

export const formatBudgetAttachmentSize = (size?: string | number, unavailableLabel = "-"): string => {
  if (!size && size !== 0) return unavailableLabel;
  const sizeNum = typeof size === "string" ? Number.parseFloat(size) : size;
  if (Number.isNaN(sizeNum)) return unavailableLabel;
  if (sizeNum < 1024) return `${sizeNum} B`;
  if (sizeNum < 1024 * 1024) return `${(sizeNum / 1024).toFixed(1)} KB`;
  return `${(sizeNum / (1024 * 1024)).toFixed(2)} MB`;
};

export const isValidBudgetAttachmentType = (file: File): boolean => {
  const fileType = file.type?.toLowerCase().trim() || "";
  const fileName = file.name.toLowerCase();
  const lastDotIndex = fileName.lastIndexOf(".");
  const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";

  if (fileType && ALLOWED_BUDGET_ATTACHMENT_TYPES.includes(fileType)) {
    return true;
  }

  return Boolean(extension && ALLOWED_BUDGET_ATTACHMENT_EXTENSIONS.includes(extension));
};

export const isPdfBudgetAttachment = (file: IBudgetAttachmentFile): boolean => {
  const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || "";
  return fileType === "application/pdf";
};

export const isSpreadsheetBudgetAttachment = (file: IBudgetAttachmentFile): boolean => {
  const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || "";
  const fileName = file.name?.toLowerCase() || "";
  return (
    fileType === "application/vnd.ms-excel" ||
    fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    fileType === "application/vnd.oasis.opendocument.spreadsheet" ||
    fileType === "text/csv" ||
    fileName.endsWith(".xls") ||
    fileName.endsWith(".xlsx") ||
    fileName.endsWith(".ods") ||
    fileName.endsWith(".csv")
  );
};

export const getBudgetAttachmentThumbnail = (file: IBudgetAttachmentFile): string => {
  const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || "";

  if (fileType.startsWith("image/")) {
    if (file.link) return file.link;
    if (file.file) return URL.createObjectURL(file.file);
  }

  if (fileType === "application/pdf") return "/assets/images/pdf-icon.svg";
  if (fileType.startsWith("video/")) return "/assets/images/video-icon.svg";
  if (fileType === "application/zip" || fileType === "application/x-rar-compressed") {
    return "/assets/images/zip-icon.svg";
  }

  return "/assets/images/file-icon.svg";
};

export const getBudgetAttachmentPreviewUrl = (file: IBudgetAttachmentFile): string | null => {
  const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || "";
  if (fileType !== "application/pdf") return null;
  if (file.link) return file.link;
  if (file.file) return URL.createObjectURL(file.file);
  return null;
};

export const isImageBudgetAttachment = (file: IBudgetAttachmentFile): boolean => {
  const fileType = file.type?.toLowerCase() || file.file?.type?.toLowerCase() || "";
  return (
    fileType.includes("png") ||
    fileType.includes("jpeg") ||
    fileType.includes("jpg") ||
    fileType.includes("gif") ||
    fileType.includes("webp")
  );
};
